import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import api from 'api';
import useGetFilterOptions from 'hooks/useGetFilterOptions';
import useMultiSort from 'hooks/useMultiSort';
import { FilterOption } from 'models/Filters';
import { ColumnFilterOption } from 'components/ReorderResizeTable/types';
import { getSortedOptionsFromStrings } from 'utils';
import {
  EnterpriseTable,
  HEADING_TRANSLATIONS,
  MULTI_SORT_DIRECTION_COPY,
  NAME_FILTER_KEY,
  PRINCIPAL_FILTER_KEY,
  SORTING_VALUES,
  TABLE_COLUMNS,
} from '../data';

export default function useEnterprisesListFilters() {
  const { t } = useTranslation();

  const { getMultiSelectSearchableFilter } = useGetFilterOptions();

  const getNameOptions = useCallback(async (searchString: string) => {
    const { data } = await api.enterprises.getEnterpriseNameOptions(
      searchString,
    );

    return getSortedOptionsFromStrings(data);
  }, []);

  const getPrincipalOptions = useCallback(async (searchString: string) => {
    const { data } = await api.enterprises.getEnterprisePrincipalOptions(
      searchString,
    );

    return getSortedOptionsFromStrings(data);
  }, []);

  const nameFilterOption = useMemo(
    () =>
      getMultiSelectSearchableFilter(
        t('name'),
        NAME_FILTER_KEY,
        NAME_FILTER_KEY,
        getNameOptions,
      ) as FilterOption,
    [getMultiSelectSearchableFilter, getNameOptions, t],
  );

  const principalFilterOption = useMemo(
    () =>
      getMultiSelectSearchableFilter(
        t('principal'),
        PRINCIPAL_FILTER_KEY,
        PRINCIPAL_FILTER_KEY,
        getPrincipalOptions,
      ) as FilterOption,
    [getMultiSelectSearchableFilter, getPrincipalOptions, t],
  );

  const filterOptions = useMemo(
    () => [nameFilterOption, principalFilterOption],
    [nameFilterOption, principalFilterOption],
  );

  const {
    multiSortValues: translatedSortOptions,
    multiSortDirectionTranslation,
  } = useMultiSort(
    TABLE_COLUMNS,
    SORTING_VALUES,
    HEADING_TRANSLATIONS,
    MULTI_SORT_DIRECTION_COPY,
  );

  const columnFilterOptions = useMemo(
    () =>
      ({
        brandName: {
          options: nameFilterOption,
        },
        principal: {
          options: principalFilterOption,
        },
        businessUnits: {
          sortDirectionTranslation:
            multiSortDirectionTranslation[SORTING_VALUES.businessUnits],
        },
        employeesCount: {
          sortDirectionTranslation:
            multiSortDirectionTranslation[SORTING_VALUES.employeesCount],
        },
        dealsCount: {
          sortDirectionTranslation:
            multiSortDirectionTranslation[SORTING_VALUES.dealsCount],
        },
      } as Partial<Record<keyof EnterpriseTable, ColumnFilterOption>>),
    [nameFilterOption, principalFilterOption, multiSortDirectionTranslation],
  );

  return {
    filterOptions,
    translatedSortOptions,
    multiSortDirectionTranslation,
    columnFilterOptions,
  };
}
